import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ApiExError, PremiumAdjustmentLimit } from '@shared';

export type { PremiumAdjustmentLimit };

export type Quote = PolicyQuote & {
  projectedCostCoverage?: number;
  discountSavings?: number;
  discountPolicyType?: number;
  roi?: number;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | undefined;
    investmentPeriodYears: number;
  };
  formattedROI?: string;
  illustrationPdf?: Blob;
  isCreatingIllustration?: boolean;
  illustrationError?: string | null;
  updatedQuote?: Quote | null;
  illustrationPdfUrl?: string | null;
  responseData?: any;
  // Set when adjustQuoteForPremium could not honor the requested premium. Drives the
  // card's amber premium indicator.
  premiumClamped?: PremiumAdjustmentLimit;
};

export interface QuoteWithROI extends Quote {
  roi?: number;
  roiValues?: any;
  formattedROI?: string;
}
export interface QuotesState {
  data: Quote[];
  hasFivePayQuotes: boolean | null;
  allPaymentOptionQuotes: Quote[];
  traditionalLTCQuotes: TraditionalLTCQuote[];
  isLoading: boolean;
  isLoadingAllPaymentOptions: boolean;
  isLoadingTraditionalLTC: boolean;
  error: QuoteError | null;
  traditionalLTCError: string | null;
}

const initialState: QuotesState = {
  data: [],
  hasFivePayQuotes: null,
  allPaymentOptionQuotes: [],
  traditionalLTCQuotes: [],
  isLoading: false,
  isLoadingAllPaymentOptions: false,
  isLoadingTraditionalLTC: false,
  error: null,
  traditionalLTCError: null,
};

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    postQuotesQueryRequest(state, action: PayloadAction<FetchQuotesPayload>) {
      state.isLoading = true;
      state.hasFivePayQuotes = null;
      state.error = null;
    },
    postQuotesQueryResponse(state, action: PayloadAction<QuotesApiResponse>) {
      state.data = action.payload.quotes as Quote[];
      state.hasFivePayQuotes = action.payload.hasFivePayQuotes ?? false;
      state.isLoading = false;
      state.error = action.payload.error;
    },
    clearQuotes(state) {
      state.data = [];
      state.hasFivePayQuotes = null;
      state.isLoading = false;
      state.error = null;
    },
    // Benefit-design mode: accumulates quotes from multiple payment-option fetches.
    // Dispatched manually (not via apiMiddleware) after direct fetch calls.
    accumulatePaymentOptionQuotes(state, action: PayloadAction<Quote[]>) {
      const existingIds = new Set(state.allPaymentOptionQuotes.map(q => q.insuranceQuoteId));
      for (const q of action.payload) {
        if (!existingIds.has(q.insuranceQuoteId)) {
          state.allPaymentOptionQuotes.push(q);
          existingIds.add(q.insuranceQuoteId);
        }
      }
      state.isLoadingAllPaymentOptions = false;
    },
    setIsLoadingAllPaymentOptions(state, action: PayloadAction<boolean>) {
      state.isLoadingAllPaymentOptions = action.payload;
    },
    clearAllPaymentOptionQuotes(state) {
      state.allPaymentOptionQuotes = [];
      state.isLoadingAllPaymentOptions = false;
    },
    postQuotesQueryFailure(state, action: PayloadAction<ApiExError>) {
      state.isLoading = false;
      state.hasFivePayQuotes = null;
      const logCode =
        typeof action.payload?.logCode === 'string' ? action.payload.logCode : undefined;
      state.error = {
        type: 'UNEXPECTED_ERROR',
        message: action.payload?.message || 'Failed to fetch quotes',
        logCode,
      };
    },
    postIllustrationByInsuranceQuoteIdRequest(
      state,
      action: PayloadAction<{
        insuranceQuoteId: string;
        zipcode: string;
        client?: { name: string; age: number; gender: string };
        partner?: { name: string; age: number; gender: string };
      }>,
    ) {
      const quote = state.data.find(q => q.insuranceQuoteId === action.payload.insuranceQuoteId);
      if (quote) {
        quote.isCreatingIllustration = true;
        quote.illustrationError = null;
      }
    },
    postIllustrationByInsuranceQuoteIdResponse(
      state,
      action: PayloadAction<{ insuranceQuoteId: string; pdf: Blob }>,
    ) {
      const quote = state.data.find(q => q.insuranceQuoteId === action.payload.insuranceQuoteId);
      if (quote) {
        quote.isCreatingIllustration = false;
        quote.illustrationPdf = action.payload.pdf;
        quote.illustrationError = null;
      }
    },
    postIllustrationByInsuranceQuoteIdFailure(state, action) {
      const apiError = action.payload;
      // Extract insuranceQuoteId from metadata if available
      const insuranceQuoteId = apiError?.metadata?.insuranceQuoteId;

      if (insuranceQuoteId) {
        const quote = state.data.find(q => q.insuranceQuoteId === insuranceQuoteId);
        if (quote) {
          quote.isCreatingIllustration = false;
          quote.illustrationError = apiError?.message || 'Failed to create illustration';
        }
      }
    },
    postTraditionalLTCQuotesQueryRequest(
      state,
      action: PayloadAction<FetchTraditionalLTCQuotesPayload>,
    ) {
      state.isLoadingTraditionalLTC = true;
      state.traditionalLTCError = null;
    },
    postTraditionalLTCQuotesQueryResponse(state, action: PayloadAction<TraditionalLTCQuote[]>) {
      state.traditionalLTCQuotes = action.payload;
      state.isLoadingTraditionalLTC = false;
      state.traditionalLTCError = null;
    },
    postTraditionalLTCQuotesQueryFailure(state, action) {
      state.isLoadingTraditionalLTC = false;
      state.traditionalLTCError =
        action.payload?.message || 'Failed to fetch Traditional LTC quotes';
    },
    updateQuoteWithIllustrationData(
      state,
      action: PayloadAction<{
        insuranceQuoteId: string;
        responseData: any;
        pdfUrl: string;
      }>,
    ) {
      const { insuranceQuoteId, responseData, pdfUrl } = action.payload;
      const quote = state.data.find(q => q.insuranceQuoteId === insuranceQuoteId);
      if (quote) {
        // Store responseData for reference
        quote.responseData = responseData;
        quote.illustrationPdfUrl = pdfUrl;

        if (responseData) {
          const parseMonetaryValue = (value: string | undefined): number | undefined => {
            if (!value) return undefined;
            const parsed = parseFloat(value.replace(/[$,]/g, ''));
            return isNaN(parsed) ? undefined : parsed;
          };
          const fieldMappings: Array<{
            responseKey: keyof typeof responseData;
            quoteKey: keyof Quote;
          }> = [
            { responseKey: 'deathBenefit', quoteKey: 'deathBenefitInitial' },
            { responseKey: 'totalInitialLTC', quoteKey: 'ltcBenefitTotal' },
            { responseKey: 'initialAnnualBenBase', quoteKey: 'ltcBenefitAnnualIndividual' },
            { responseKey: 'initialMonthlyBenBase', quoteKey: 'ltcBenefitMonthlyIndividual' },
          ];

          const updatedFields = fieldMappings.reduce((acc, { responseKey, quoteKey }) => {
            const parsedValue = parseMonetaryValue(responseData[responseKey]);
            if (parsedValue !== undefined) {
              acc[quoteKey] = parsedValue;
            }
            return acc;
          }, {} as Partial<Quote>);

          const updatedQuote: Quote = {
            ...quote,
            ...updatedFields,
          };

          quote.updatedQuote = updatedQuote;
        }
      }
    },
  },
});

export const {
  postQuotesQueryRequest,
  postQuotesQueryResponse,
  postQuotesQueryFailure,
  clearQuotes,
  accumulatePaymentOptionQuotes,
  setIsLoadingAllPaymentOptions,
  clearAllPaymentOptionQuotes,
  postIllustrationByInsuranceQuoteIdRequest,
  postIllustrationByInsuranceQuoteIdResponse,
  postIllustrationByInsuranceQuoteIdFailure,
  postTraditionalLTCQuotesQueryRequest,
  postTraditionalLTCQuotesQueryResponse,
  postTraditionalLTCQuotesQueryFailure,
  updateQuoteWithIllustrationData,
} = quotesSlice.actions;

export const quotesReducer = quotesSlice.reducer;
